import React, { useEffect, useRef, useState } from "react";

import { isNullOrUndefined, isWithinBounds, useGetFootprintingBeefPrimaryDataById, useMutateFootprintingBeefPrimaryData, useTranslation } from "utils";

import { ENUM_FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION, FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION } from "models";

import { Card, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

type BeefYieldFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const BeefYieldForm = ({ primaryDataId, readOnly, farmId, year }: BeefYieldFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [meatProduction, setMeatProduction] = useState<ENUM_FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION | null>(null);
  const [beefYieldKg, setBeefYieldKg] = useState<number | null>(null);

  const { data: beefPrimaryData, isLoading, isSuccess } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateBeefPrimaryData } = useMutateFootprintingBeefPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      meatProduction,
      beefYieldKg,
      isSubmitted: false,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateBeefPrimaryData({});
      }
    },
    [isLoading, mutateBeefPrimaryData, readOnly]
  );

  useEffect(() => {
    if (beefPrimaryData && isSuccess) {
      setMeatProduction(beefPrimaryData.meatProduction ?? null);
      setBeefYieldKg(beefPrimaryData.beefYieldKg ?? null);

      formRef.current?.setFieldsValue({
        beefYieldKg: beefPrimaryData.beefYieldKg ?? null,
      });

      if (beefPrimaryData.beefYieldKg != null && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [beefPrimaryData, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-beef-yield-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("beefYieldForm.radioQuestion")}
              {true && <span className="required-asterisk">*</span>}
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-beef-yield-production-radio"
              onChange={({ target }) => setMeatProduction(target.value)}
              value={meatProduction}
              optionType="button"
              options={[FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION.LIVE_WEIGHT, FARM_FOOTPRINTING_BEEF_MEAT_PRODUCTION.SLAUGHTER_WEIGHT].map((option) => ({
                label: t(`beefYieldForm.radioOptions.${option}`),
                value: option,
              }))}
            />
          </Flex>
          <InputNumber
            disabled={readOnly}
            name="beefYieldKg"
            dataTestId="footprinting-beef-yield-weight-input"
            label={t("beefYieldForm.question.beefYieldAmountLabel")}
            suffix={t("beefYieldForm.question.beefYieldAmountSuffix")}
            value={beefYieldKg}
            initialValue={beefYieldKg}
            onChange={(v) => setBeefYieldKg(v)}
            validateFirst
            rules={[
              { required: true, message: t("beefYieldForm.error.beefYieldAmount") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isWithinBounds(v, 1, 16000)) {
                    return Promise.reject(new Error(t("beefYieldForm.error.beefYieldAmount")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
        </Flex>
      </Form>
    </Card>
  );
};

BeefYieldForm.Key = "beef.beefYield";
export { BeefYieldForm };
